import { SectionProps } from "../Section";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import {
	Button,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemSecondaryAction,
	ListItemText,
	makeStyles,
	Typography
} from "@material-ui/core";
import clsx from "clsx";
import {
	AddCircleOutlineOutlined,
	DeleteOutline,
	CancelOutlined
} from "@material-ui/icons";
import React, { useCallback, useMemo } from "react";
import { TextField } from "@ploy-ui/form-fields";
import { InputFieldProps } from "../..";
import { Formik, Field as FormikField } from "formik";
import * as Yup from "yup";
import { ErrorDisplay } from "@ploy-lib/types";
import { defineMessages, useIntl } from "react-intl";

addRegisteredSectionLayout({
	name: "ProbateSettlementSection",
	displayName: "ProbateSettlementSection",
	settings: {
		editableOptions: {}
	}
});

const messages = defineMessages({
	captionTitle: {
		defaultMessage: "Who will you release?",
		id: "template-form.field.probateSettlements.captionTitle"
	},
	addButton: {
		defaultMessage: "Add",
		id: "template-form.field.probateSettlements.addButton"
	},
	addPerson: {
		defaultMessage: "Add new person",
		id: "template-form.field.probateSettlements.addPerson"
	},
	nameLabel: {
		defaultMessage: "Name",
		id: "template-form.field.probateSettlements.nameLabel"
	},
	validationMessage: {
		defaultMessage: "Field is required",
		id: "template-form.field.probateSettlements.validationMessage"
	}
});

const useStyles = makeStyles(
	theme => ({
		root: {
			flexDirection: "column"
		},
		boxes: {
			border: "1px solid #757575",
			borderRadius: "4px",
			backgroundColor: "white"
		},
		list: {},
		listItem: { marginBottom: theme.spacing(1.5) },
		button: {
			padding: theme.spacing(1) + "px " + theme.spacing(2) + "px",
			justifyContent: "start"
		},
		icon: {
			height: theme.typography.button.lineHeight,
			width: theme.typography.button.lineHeight
		},
		addNames: {
			paddingTop: theme.spacing(6) + "px !important",
			paddingBottom: theme.spacing(4) + "px !important",
			backgroundColor: theme.palette.primary.light,
			borderColor: theme.palette.secondary.light,
			borderWidth: 1,
			borderTopStyle: "solid",
			borderBottomStyle: "solid",
			rowGap: theme.spacing(2)
		}
	}),
	{ name: "ProbateSettlementSection" }
);

const CALCRULES_FIELDNAME_ProbateSettlements =
	"PRIVATE_CUSTOMER_DATA.ProbateSettlements";

const ProbateSettlementSection = (sectionProps: SectionProps) => {
	return (
		<>
			<FormikField
				component={ProbateSettlementSectionInternal}
				name={CALCRULES_FIELDNAME_ProbateSettlements}
				className={sectionProps.className}
			/>
		</>
	);
};

interface ProbateSettlementSectionProps
	extends Pick<SectionProps, "className">,
		InputFieldProps {}

const ProbateSettlementSectionInternal = (
	props: ProbateSettlementSectionProps
) => {
	const styles = useStyles(props);
	const intl = useIntl();
	const [addNewPerson, setAddNewPerson] = React.useState(false);

	// The actual variable stored in CalcRules:
	const names: string = useMemo(
		() => props.field.value || "",
		[props.field.value]
	);

	// Helper-variable used for display and data-manipulation
	const namesArr: string[] = useMemo(
		() => (names.length > 0 ? names.split(";") : []),
		[names]
	);

	const addItem: (newItem: string) => void = useCallback(
		(newItem: string) => {
			const updatedNamesString = namesArr.concat(newItem).join(";");
			props.form.setFieldValue(
				CALCRULES_FIELDNAME_ProbateSettlements,
				updatedNamesString
			);
		},
		[namesArr, props.form]
	);
	const deleteItem: (indexToRemove: number) => void = useCallback(
		(indexToRemove: number) => {
			const updatedNamesString = namesArr
				.filter((_, i) => i !== indexToRemove)
				.join(";");
			props.form.setFieldValue(
				CALCRULES_FIELDNAME_ProbateSettlements,
				updatedNamesString
			);
		},
		[namesArr, props.form]
	);

	const probateSettlementValidationSchema: Yup.ObjectSchema<{ name: string }> =
		Yup.object({
			name: Yup.string().required(
				intl.formatMessage(messages.validationMessage)
			)
		});

	return (
		<Grid
			container
			item
			spacing={1}
			className={clsx(styles.root, props.className)}
		>
			<Grid item xs={12}>
				<Typography variant="caption">
					{intl.formatMessage(messages.captionTitle)}
				</Typography>
			</Grid>
			{namesArr.length > 0 && (
				<Grid item xs={12}>
					<List className={clsx(styles.list)}>
						{namesArr.map((name, i) => (
							<ListItem
								className={clsx(styles.listItem, styles.boxes)}
								key={name + i.toString()}
							>
								<ListItemText primary={name}></ListItemText>
								<ListItemSecondaryAction>
									<IconButton
										onClick={() => {
											deleteItem(i);
										}}
									>
										<DeleteOutline />
									</IconButton>
								</ListItemSecondaryAction>
							</ListItem>
						))}
					</List>
				</Grid>
			)}

			{addNewPerson ? (
				<Grid item xs={12}>
					<Formik
						validationSchema={probateSettlementValidationSchema}
						onSubmit={(newEntry: { name: string }, _: any) => {
							addItem(newEntry.name);
						}}
						initialValues={{ name: "" }}
					>
						{formikProps => (
							<Grid container item className={clsx(styles.addNames)}>
								<FormikField
									name="name"
									component={TextField}
									errorDisplay={ErrorDisplay.Touched}
									fullWidth
									label={intl.formatMessage(messages.nameLabel)}
									style={{ padding: 4 }}
								/>
								<Grid
									item
									container
									direction="row"
									justifyContent="space-between"
								>
									<Button
										variant="contained"
										onClick={() => {
											setAddNewPerson(false);
											formikProps.submitForm();
										}}
										disabled={
											!formikProps.isValid ||
											formikProps.values.name.length === 0
										}
									>
										{intl.formatMessage(messages.addButton)}
									</Button>
									<IconButton>
										<CancelOutlined
											className={styles.icon}
											onClick={() => setAddNewPerson(false)}
										/>
									</IconButton>
								</Grid>
							</Grid>
						)}
					</Formik>
				</Grid>
			) : (
				<Grid item xs={12}>
					<Button
						fullWidth
						variant="outlined"
						startIcon={<AddCircleOutlineOutlined className={styles.icon} />}
						className={clsx(styles.button, styles.boxes)}
						onClick={() => {
							setAddNewPerson(true);
						}}
					>
						{intl.formatMessage(messages.addPerson)}
					</Button>
				</Grid>
			)}
		</Grid>
	);
};

ProbateSettlementSection.displayName = "ProbateSettlementSection";

export { ProbateSettlementSection };
